/**
 * 백엔드 AI 비동기 작업(/admin/ai/jobs) 폴링 도우미.
 * AI 호출은 문항당 30초~2분 걸려 API 제한 시간(30초)을 넘기므로,
 * 요청은 jobId만 받고 결과는 이 함수로 기다립니다.
 */
export type AiJobType = 'analyze-question' | 'validate-batch' | 'bulk-analyze' | 'classify-domain' | 'quality-review';
export type AiJobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

export interface AiJobCounts {
  processed: number;
  succeeded: number;
  failed: number;
  aiPassed?: number;
  reviewRequired?: number;
  flagged?: number;
  clean?: number;
}

export interface AiJob<TResult = unknown> {
  job_id: string;
  type: AiJobType;
  status: AiJobStatus;
  exam_type?: string;
  total?: number;
  cursor?: number;
  counts?: AiJobCounts;
  recent_results?: Array<Record<string, unknown>>;
  result?: TResult;
  error?: string;
  created_at: string;
  updated_at: string;
  finished_at?: string;
}

export const isFinished = (job?: Pick<AiJob, 'status'> | null) =>
  Boolean(job && ['succeeded', 'failed', 'cancelled'].includes(job.status));

type Request = (url: string, options?: RequestInit) => Promise<Response>;

export async function fetchAiJob<T = unknown>(request: Request, jobId: string): Promise<AiJob<T>> {
  const response = await request(`/admin/ai/jobs/${jobId}`);
  const body = await response.json().catch(() => null);
  if (!response.ok || !body?.success) {
    throw new Error(body?.error?.message || `작업 상태를 불러오지 못했습니다. (HTTP ${response.status})`);
  }
  return body.data as AiJob<T>;
}

export async function cancelAiJob(request: Request, jobId: string): Promise<void> {
  await request(`/admin/ai/jobs/${jobId}/cancel`, { method: 'POST' });
}

/** 끝날 때까지 기다립니다. 진행 상황은 onProgress로 받고, signal로 기다리기만 멈출 수 있습니다(작업은 계속됨). */
export async function waitForAiJob<T = unknown>(
  request: Request,
  jobId: string,
  { onProgress, intervalMs = 3000, signal }: { onProgress?: (job: AiJob<T>) => void; intervalMs?: number; signal?: AbortSignal } = {},
): Promise<AiJob<T>> {
  let failures = 0;
  for (;;) {
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
    try {
      const job = await fetchAiJob<T>(request, jobId);
      failures = 0;
      onProgress?.(job);
      if (isFinished(job)) return job;
    } catch (error) {
      // 일시적인 네트워크 오류는 몇 번까지 참고 계속 기다립니다.
      failures += 1;
      if (failures >= 5) throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

/** 이 시험 유형에서 아직 진행 중인 일괄 작업 (새로고침 후 이어 보기용) */
export async function findActiveBatchJob(request: Request, examType: string): Promise<AiJob | null> {
  const response = await request(`/admin/ai/jobs?examType=${encodeURIComponent(examType)}&limit=10`);
  const body = await response.json().catch(() => null);
  if (!response.ok || !body?.success) return null;
  const jobs = (body.data?.jobs || []) as AiJob[];
  return jobs.find((job) => job.type !== 'analyze-question' && !isFinished(job)) || null;
}
